import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Gauge, Zap, Clock, PhoneOff, Layers, TrendingUp, AlertTriangle, Save, Loader2, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const Kpi = ({ icon: Icon, label, value, sub, tone = 'text-teal-300', testid }) => (
  <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-4" data-testid={testid}>
    <Icon size={16} className={tone} />
    <p className="text-xl font-semibold text-white mt-2 tabular-nums">{value}</p>
    <p className="text-xs text-gray-400">{label}</p>
    {sub && <p className="text-[11px] text-gray-500 mt-0.5">{sub}</p>}
  </div>
);

const BurstStatus = ({ job, onStart }) => {
  const running = job?.status === 'running';
  const done = job?.status === 'done';
  const allOk = done && !job.failed;
  const tone = running ? 'border-sky-400/30 bg-sky-400/10 text-sky-200'
    : allOk ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
    : done || job?.status === 'interrupted' ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
    : 'border-white/10 text-gray-300';
  const text = running ? `Turning on bursting… ${job.done || 0} of ${job.total} agents`
    : allOk ? `Bursting ON for all ${job.updated} agents · ${new Date(job.finished_at).toLocaleString()}`
    : done ? `Bursting ON for ${job.updated} of ${job.done} agents · ${job.failed} failed — run again`
    : job?.status === 'interrupted' ? 'Last run was interrupted — run again'
    : 'Bursting not applied to existing agents yet';
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className={`min-h-9 py-1.5 px-3 rounded-md border text-sm flex items-center gap-1.5 ${tone}`} data-testid="capacity-bursting-status">
        {running ? <Loader2 size={14} className="animate-spin" /> : allOk ? <CheckCircle2 size={14} /> : <Zap size={14} />}{text}
      </span>
      {!running && (
        <button type="button" onClick={onStart} className="h-9 px-3 rounded-md border border-white/10 text-sm text-gray-200 hover:bg-white/5 flex items-center gap-1.5" data-testid="capacity-enable-bursting">
          <Zap size={14} />{allOk ? 'Re-apply to all agents' : 'Turn on bursting for all agents'}
        </button>
      )}
    </div>
  );
};

const Settings = ({ settings, onSaved }) => {
  const { api } = useAuth();
  const [v, setV] = useState(settings);
  const save = async () => {
    try { await api.put('/api/admin/capacity/settings', v); toast.success('Capacity settings saved'); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const burst = async () => {
    try { await api.post('/api/admin/capacity/enable-bursting'); toast.success('Started — progress shows next to the button'); onSaved(); }
    catch { toast.error('Could not start the update'); }
  };
  const field = (k, label) => (
    <label className="text-xs text-gray-400 flex flex-col gap-1">{label}
      <input type="number" min={0} value={v[k]} onChange={(e) => setV({ ...v, [k]: Number(e.target.value) })}
        className="w-28 h-9 px-2 rounded-md bg-white/[0.04] border border-white/10 text-white text-sm" data-testid={`capacity-${k}-input`} />
    </label>
  );
  return (
    <div className="flex items-end gap-4 flex-wrap">
      {field('el_limit', 'ElevenLabs lines (plan limit)')}
      {field('inbound_reserve', 'Lines kept free for inbound')}
      <button type="button" onClick={save} className="h-9 px-3 rounded-md bg-teal-400 text-[#06201d] text-sm font-semibold flex items-center gap-1.5" data-testid="capacity-save"><Save size={14} />Save</button>
      <BurstStatus job={settings.bursting_job} onStart={burst} />
    </div>
  );
};

export const CapacityPanel = () => {
  const { api } = useAuth();
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get('/api/admin/capacity').then(r => setD(r.data)).catch(() => {}), [api]);
  const running = d?.settings?.bursting_job?.status === 'running';
  useEffect(() => { load(); const id = setInterval(load, running ? 2000 : 10000); return () => clearInterval(id); }, [load, running]);
  if (!d) return null;
  const pct = Math.min(100, d.usage_pct);
  return (
    <section className="glass-card rounded-xl p-5 mb-6" data-testid="admin-capacity-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-gray-500 font-semibold">Platform capacity · live</p>
          <h3 className="text-lg font-semibold text-white">{d.live.total} live calls <span className="text-gray-500">/ {d.el_limit} ElevenLabs lines</span></h3>
          <p className="text-xs text-gray-400">Bursting {d.settings.bursting ? `on — up to ${d.burst_cap} at double cost` : 'off'} · outbound stops at {d.outbound_cap} so {d.settings.inbound_reserve} stay free for inbound</p>
        </div>
        {d.alert && <span className="flex items-center gap-1.5 text-xs text-amber-200 bg-amber-400/10 border border-amber-400/30 rounded-full px-3 py-1" data-testid="capacity-alert"><AlertTriangle size={13} />Above 80% of your lines</span>}
      </div>
      <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden mb-5">
        <div className={`h-full transition-[width] duration-700 ${pct >= 100 ? 'bg-rose-400' : pct >= 80 ? 'bg-amber-400' : 'bg-teal-400'}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
        <Kpi icon={Gauge} label="Live now" value={d.live.total} sub={`${d.live.inbound} in · ${d.live.outbound} out · ${d.live.web} web`} testid="capacity-live" />
        <Kpi icon={Zap} label="In burst (2× cost)" value={d.in_burst} tone="text-amber-300" testid="capacity-burst" />
        <Kpi icon={Clock} label="Outbound queued" value={d.queued} tone="text-sky-300" testid="capacity-queued" />
        <Kpi icon={PhoneOff} label="Inbound rejected · 24h" value={d.rejected_24h} tone="text-rose-300" testid="capacity-rejected" />
        <Kpi icon={Layers} label="Extra lines sold" value={d.lines_sold} sub={`$${d.lines_sold * 8}/mo · ${d.sold_capacity} lines promised`} testid="capacity-sold" />
        <Kpi icon={TrendingUp} label="Busiest moment · 7d" value={d.peak.max} sub={d.peak.at ? new Date(d.peak.at).toLocaleString() : 'No calls yet'} testid="capacity-peak" />
      </div>
      {d.upgrade_hint && <p className="text-sm text-amber-200 mb-4" data-testid="capacity-upgrade-hint">{d.upgrade_hint}</p>}
      {d.by_agency.length > 0 && (
        <div className="overflow-x-auto mb-5">
          <table className="w-full text-sm" data-testid="capacity-by-agency">
            <thead><tr className="text-left text-xs text-gray-500"><th className="py-2">Agency</th><th>Live</th><th>In</th><th>Out</th><th>Web</th><th>Limit</th></tr></thead>
            <tbody>{d.by_agency.map(r => (
              <tr key={r.workspace_id} className="border-t border-white/[0.05] text-gray-300">
                <td className="py-2 text-white">{r.name}</td><td>{r.total}</td><td>{r.inbound}</td><td>{r.outbound}</td><td>{r.web}</td>
                <td className={r.total >= r.limit ? 'text-rose-300' : ''}>{r.limit}</td>
              </tr>))}
            </tbody>
          </table>
        </div>
      )}
      <Settings settings={d.settings} onSaved={load} />
    </section>
  );
};

export default CapacityPanel;
